import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCheck, FaTimes, FaQuestion } from 'react-icons/fa';

const Pricing = () => {
  const [showMonthly, setShowMonthly] = useState(false);
  const [showFaq, setShowFaq] = useState(null);
  
  const packages = [
    {
      name: "Basic",
      description: "Perfect for small businesses and personal websites",
      price: {
        oneTime: "₹8,000",
        monthly: "₹1,000",
      },
      features: [
        { text: "Responsive Design", included: true },
        { text: "Up to 5 Pages", included: true },
        { text: "Contact Form", included: true },
        { text: "SEO Optimization", included: true },
        { text: "1 Month Support", included: true },
        { text: "Content Management System", included: false },
        { text: "E-commerce Functionality", included: false },
        { text: "Custom Animations", included: false },
      ],
      color: "cyber-neon",
      popular: false
    },
    {
      name: "Pro",
      description: "Ideal for growing businesses and e-commerce sites",
      price: {
        oneTime: "₹15,000",
        monthly: "₹1,800", 
      },
      features: [
        { text: "Responsive Design", included: true },
        { text: "Up to 10 Pages", included: true },
        { text: "Contact Form", included: true },
        { text: "SEO Optimization", included: true },
        { text: "3 Months Support", included: true },
        { text: "Content Management System", included: true },
        { text: "E-commerce Functionality", included: true },
        { text: "Custom Animations", included: false },
      ],
      color: "cyber-pink",
      popular: true
    },
    {
      name: "Premium",
      description: "Complete solution for large businesses with complex needs",
      price: {
        oneTime: "₹25,000",
        monthly: "₹2,500",
      },
      features: [
        { text: "Responsive Design", included: true },
        { text: "Unlimited Pages", included: true },
        { text: "Contact Form", included: true },
        { text: "Advanced SEO Optimization", included: true },
        { text: "6 Months Support", included: true },
        { text: "Content Management System", included: true },
        { text: "E-commerce Functionality", included: true },
        { text: "Custom Animations", included: true },
      ],
      color: "cyber-purple",
      popular: false
    }
  ];

  const faqs = [
    {
      question: "What's included in the initial price?",
      answer: "Our prices include website design, development, testing, and deployment. We also provide basic SEO setup, responsive design for all devices, and initial content integration."
    },
    {
      question: "How long does it take to build a website?",
      answer: "Typically, our projects take 2-4 weeks from start to finish, depending on the complexity and scope. Basic websites can be completed in as little as 1-2 weeks, while more complex e-commerce sites may take 4-6 weeks."
    },
    {
      question: "Do you offer payment plans?",
      answer: "Yes, we offer flexible payment options. Standard terms are 50% upfront and 50% upon completion, but we can also arrange monthly payment plans for larger projects."
    },
    {
      question: "Can I upgrade my package later?",
      answer: "Absolutely! Our packages are designed to scale with your business. You can start with a basic package and upgrade as your needs grow. We'll make the transition seamless."
    },
    {
      question: "What happens after my website is launched?",
      answer: "After launch, we provide a support period (varying by package) for bug fixes and minor updates. For ongoing maintenance and updates, we offer monthly maintenance packages or hourly support options."
    }
  ];

  return (
    <div className="min-h-screen pt-28 pb-20 bg-cyber-dark">
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl font-orbitron font-bold mb-6">
            Transparent <span className="text-cyber-pink">Pricing</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Choose a plan that works for you. All packages include responsive design, SEO optimization, and post-launch support.
          </p>
        </motion.div>

        {/* Pricing Toggle */}
        <div className="flex justify-center mb-12">
          <div className="bg-cyber-gray p-1 rounded-lg inline-flex">
            <button 
              className={`px-6 py-2 rounded font-medium transition-all duration-300 ${!showMonthly ? 'bg-cyber-pink text-white' : 'text-gray-300 hover:text-white'}`}
              onClick={() => setShowMonthly(false)}
            >
              One-time Payment
            </button>
            <button 
              className={`px-6 py-2 rounded font-medium transition-all duration-300 ${showMonthly ? 'bg-cyber-pink text-white' : 'text-gray-300 hover:text-white'}`}
              onClick={() => setShowMonthly(true)}
            >
              Monthly Subscription
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {packages.map((pkg, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className={`relative overflow-hidden ${pkg.popular ? 'transform md:-translate-y-4' : ''}`}
            >
              {pkg.popular && (
                <div className="absolute top-0 right-0 bg-cyber-pink text-white font-orbitron text-xs tracking-wider py-1 px-3 uppercase z-10">
                  Most Popular
                </div>
              )}
              
              <div className={`cyber-card p-8 border-2 border-${pkg.color} h-full flex flex-col`}>
                <div className="text-center mb-8">
                  <h2 className={`text-3xl font-orbitron font-bold mb-2 text-${pkg.color}`}>{pkg.name}</h2>
                  <p className="text-gray-300 mb-6">{pkg.description}</p>
                  <div className="text-4xl font-orbitron font-bold text-white mb-2">
                    {showMonthly ? pkg.price.monthly : pkg.price.oneTime}
                  </div>
                  <p className="text-sm text-gray-400">
                    {showMonthly ? 'per month' : 'one-time payment'}
                  </p>
                </div>
                
                <div className="flex-grow">
                  <ul className="space-y-3 mb-8">
                    {pkg.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        {feature.included ? (
                          <FaCheck className={`text-${pkg.color} mt-1 mr-2 flex-shrink-0`} />
                        ) : (
                          <FaTimes className="text-gray-500 mt-1 mr-2 flex-shrink-0" />
                        )}
                        <span className={feature.included ? 'text-gray-200' : 'text-gray-500'}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <Link 
                  to="/contact" 
                  className={`text-center py-3 px-6 rounded font-medium bg-${pkg.color} hover:bg-${pkg.color}/80 text-black transition-colors duration-300 block w-full font-orbitron`}
                >
                  Choose {pkg.name}
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Custom Request */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="cyber-card p-8 text-center mb-20"
        >
          <h2 className="text-3xl font-orbitron font-bold mb-4">Need a <span className="text-cyber-neon">Custom</span> Solution?</h2>
          <p className="text-gray-300 text-lg mb-6 max-w-3xl mx-auto">
            Don't see a package that fits your needs? We offer custom solutions tailored to your specific requirements.
          </p>
          <Link to="/contact" className="cyber-button inline-block">
            <span>Contact Us</span>
          </Link>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <h2 className="text-3xl font-orbitron font-bold mb-8 text-center">Frequently <span className="text-cyber-pink">Asked</span> Questions</h2>
          
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="cyber-card overflow-hidden">
                <button 
                  className="w-full text-left p-6 flex justify-between items-center"
                  onClick={() => setShowFaq(showFaq === index ? null : index)}
                >
                  <h3 className="text-xl text-white font-medium flex items-center">
                    <FaQuestion className="text-cyber-neon mr-3 flex-shrink-0" />
                    <span>{faq.question}</span>
                  </h3>
                  <span className={`transform transition-transform duration-300 ${showFaq === index ? 'rotate-180' : ''}`}>
                    <svg className="w-6 h-6 text-cyber-pink" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </span>
                </button>
                
                {showFaq === index && (
                  <motion.div 
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-6 pb-6"
                  >
                    <div className="border-t border-gray-700 pt-4">
                      <p className="text-gray-300">{faq.answer}</p>
                    </div>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Pricing; 